import { useEffect, useState } from 'react';
import { apiGet, AdminForbiddenError } from '../lib/api';

type Tenant = {
  id: string;
  slug: string;
  name: string;
  tier: string;
  status: string;
  created_at: string;
  owner_email: string | null;
  mrr_cents: number | null;
};

function formatMrr(cents: number | null): string {
  if (cents === null) return '—';
  return `$${(cents / 100).toFixed(2)}`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-AU', { dateStyle: 'medium' });
}

export function Tenants() {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);

  useEffect(() => {
    apiGet<{ tenants: Tenant[] }>('/api/admin/tenants')
      .then(({ tenants: rows }) => setTenants(rows))
      .catch((err: unknown) => {
        if (err instanceof AdminForbiddenError) {
          setForbidden(true);
        } else {
          setError(err instanceof Error ? err.message : 'unknown error');
        }
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-slate-500">
        Loading…
      </div>
    );
  }

  if (forbidden) {
    return (
      <div className="rounded-lg border border-red-700/40 bg-red-900/10 p-6 text-center">
        <p className="text-red-400">
          You don&apos;t have admin access. Sign in with an admin account.
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-700/40 bg-red-900/10 p-6 text-center">
        <p className="text-red-400">{error}</p>
      </div>
    );
  }

  if (tenants.length === 0) {
    return (
      <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-6 text-center text-slate-500">
        No tenants yet
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-slate-800">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-800 bg-slate-900/60 text-left text-xs uppercase tracking-widest text-slate-400">
            <th className="px-4 py-3">Slug</th>
            <th className="px-4 py-3">Name</th>
            <th className="px-4 py-3">Tier</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3">Owner Email</th>
            <th className="px-4 py-3">MRR</th>
            <th className="px-4 py-3">Created</th>
          </tr>
        </thead>
        <tbody>
          {tenants.map((t) => (
            <tr
              key={t.id}
              className="border-b border-slate-800/60 hover:bg-slate-900/40 transition-colors"
            >
              <td className="px-4 py-3 font-mono text-slate-300">{t.slug}</td>
              <td className="px-4 py-3">{t.name}</td>
              <td className="px-4 py-3 capitalize">{t.tier}</td>
              <td className="px-4 py-3">
                <span
                  className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                    t.status === 'active'
                      ? 'bg-emerald-900/40 text-emerald-400'
                      : t.status === 'trial'
                        ? 'bg-sky-900/40 text-sky-400'
                        : 'bg-slate-800 text-slate-400'
                  }`}
                >
                  {t.status}
                </span>
              </td>
              <td className="px-4 py-3 text-slate-400">{t.owner_email ?? '—'}</td>
              <td className="px-4 py-3 tabular-nums">{formatMrr(t.mrr_cents)}</td>
              <td className="px-4 py-3 text-slate-400">{formatDate(t.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
